import React, { useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import * as S from './styles';
import ButtonSidebar from 'components/ButtonSidebar';
import Text from 'components/Text';
import { useMenuSelection } from 'global/MenuSelectionContext';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { MenuItem, isMeetingRoomExtras } from 'types/menuTypes';

interface IModalSubMenu {
  closeSubMenu: () => void;
  subMenu: MenuItem[];
  title: string;
  open: boolean;
  isSubMenuActive?: boolean;
  level: 'first' | 'second' | 'third';
}

const ModalSubMenu = ({
  closeSubMenu,
  subMenu,
  title,
  open,
  isSubMenuActive = false,
  level,
}: IModalSubMenu) => {
  const {
    setFirstLevelMenu,
    setSecondLevelMenu,
    setThirdLevelMenu,
    selectMenuByDocumentId,
    activeDocumentId,
  } = useMenuSelection();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => {
    // Só fecha se não estivermos em segundo ou terceiro nível E não tivermos menu ativo
    if (!isSubMenuActive && level === 'first') {
      console.log('[ModalSubMenu] Fechando por clique fora - nível:', level);
      closeSubMenu();
    }
  });

  const handleSubMenuClick = useCallback(
    (path: string, disabled: boolean, menuItem: MenuItem, level: 'first' | 'second' | 'third') => {
      if (disabled) return;
      console.log('[ModalSubMenu][DEBUG] Clique:', {
        level,
        menuItem,
        featureDocumentId: menuItem.feature?.documentId,
        dynamicComponent: menuItem.feature?.dynamic_component,
        path,
      });

      const isMeetingRoom = menuItem.feature?.dynamic_component === 'MEETING_ROOM';
      const isMeetingRoomRoom = menuItem.feature?.dynamic_component === 'MEETING_ROOM_ROOM';

      // Se for um menu de reunião (MEETING_ROOM), navega para o primeiro submenu
      if (isMeetingRoom && menuItem.subMenu && menuItem.subMenu.length > 0) {
        const firstSubMenu = menuItem.subMenu[0];
        let documentId = menuItem.documentId || '';
        if (!documentId && firstSubMenu.extras) {
          const extras = firstSubMenu.extras;
          if (isMeetingRoomExtras(extras)) {
            documentId = extras.internal_id || '';
          }
        }
        // Sempre passa o path específico para garantir navegação correta
        selectMenuByDocumentId(documentId, true, undefined, firstSubMenu.path);
        navigate(firstSubMenu.path);
        console.log('[ModalSubMenu] Navegou para submenu de reunião:', firstSubMenu);
        return;
      }

      // Se for uma sala específica (MEETING_ROOM_ROOM)
      if (isMeetingRoomRoom) {
        let documentId = menuItem.documentId || '';
        if (!documentId && menuItem.extras) {
          const extras = menuItem.extras;
          if (isMeetingRoomExtras(extras)) {
            documentId = extras.internal_id || '';
          }
        }
        // Sempre passa o path específico para garantir navegação correta
        selectMenuByDocumentId(documentId, true, undefined, menuItem.path);
        navigate(menuItem.path);
        console.log('[ModalSubMenu] Navegou para sala de reunião:', menuItem);
        return;
      }

      // Para outros tipos de menu - PERMITE SELEÇÃO DIRETA
      if (menuItem.feature?.documentId) {
        // Se o menu tem feature.documentId, navega diretamente para ele
        selectMenuByDocumentId(menuItem.feature.documentId, true, undefined, menuItem.path);
        navigate(menuItem.path);
        console.log('[ModalSubMenu] Navegou diretamente para menu:', menuItem);

        // Só fecha se estivermos no primeiro nível
        if (level === 'first') {
          console.log('[ModalSubMenu] Fechando ModalSubMenu após navegação - nível:', level);
          closeSubMenu();
        } else {
          console.log('[ModalSubMenu] Mantendo ModalSubMenu aberto após navegação - nível:', level);
        }
        return;
      }

      // Se não tem feature.documentId mas tem submenu, navega para o primeiro submenu
      const hasValidSubMenu =
        menuItem.subMenu && menuItem.subMenu.length > 0 && menuItem.subMenu[0]?.feature?.documentId;
      if (hasValidSubMenu && menuItem.subMenu) {
        const firstSubMenu = menuItem.subMenu[0];
        selectMenuByDocumentId(
          firstSubMenu.feature?.documentId || '',
          true,
          undefined,
          firstSubMenu.path
        );
        navigate(firstSubMenu.path);
        console.log('[ModalSubMenu] Navegou para primeiro submenu:', firstSubMenu);
      }
    },
    [navigate, selectMenuByDocumentId, closeSubMenu]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent, path: string, disabled: boolean, menuItem: MenuItem) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSubMenuClick(path, disabled, menuItem, level);
      }
    },
    [handleSubMenuClick, level]
  );

  // Renderiza apenas menus de segundo nível (filhos diretos do menu de primeiro nível)
  const memoizedSubMenu = React.useMemo(() => {
    console.log('[ModalSubMenu][DEBUG] Filtrando menus:', {
      totalMenus: subMenu.length,
      menuLevels: subMenu.map(item => ({ label: item.label, level: item.level })),
    });

    const filtered = subMenu.filter(item => item.level === 'second');

    console.log('[ModalSubMenu][DEBUG] Menus filtrados:', {
      filteredCount: filtered.length,
      filteredMenus: filtered.map(item => ({ label: item.label, level: item.level })),
    });

    return filtered;
  }, [subMenu]);

  // Só renderiza se o menu for de primeiro nível e tiver submenus de segundo nível
  if (!open || !subMenu || subMenu.length === 0 || (subMenu[0] && subMenu[0].level !== 'second'))
    return null;

  return (
    <S.Container ref={ref} open={open} role="menu" aria-label={title}>
      <div className="header">
        <Text color="primary" size="medium" textAlign="center">
          {title}
        </Text>
      </div>
      {memoizedSubMenu.map((menu, index) => (
        <div
          key={`${menu.feature?.documentId || menu.path}-${index}`}
          onClick={() => handleSubMenuClick(menu.path, !menu.isActive, menu, level)}
          onKeyPress={e => handleKeyPress(e, menu.path, !menu.isActive, menu)}
          role="menuitem"
          tabIndex={0}
          aria-disabled={!menu.isActive}
        >
          <ButtonSidebar
            label={menu.label}
            path={menu.path}
            icon={menu.icon}
            isActive={menu.isActive}
            disabled={!menu.isActive}
            subMenu={menu.subMenu || []}
            isSubMenu
            level={menu.level}
          />
        </div>
      ))}
    </S.Container>
  );
};

export default React.memo(ModalSubMenu);
